import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Badge, Form, Alert, Spinner } from 'react-bootstrap';
import { ArrowLeft, Printer } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getDeliveryNoteById, updateDeliveryNoteStatus } from '../../api/orders';

const DeliveryNoteDetails = ({ deliveryNoteId, onCancel }) => {
    const [deliveryNote, setDeliveryNote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [setUpdating] = useState(false);
    const [newStatus, setNewStatus] = useState('');

    useEffect(() => {
        const fetchDeliveryNote = async () => {
            try {
                setLoading(true);
                const response = await getDeliveryNoteById(deliveryNoteId);
                setDeliveryNote(response);
                setNewStatus(response.status);
            } catch (err) {
                setError('Erreur lors du chargement du bon de livraison');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (deliveryNoteId) {
            fetchDeliveryNote();
        }
    }, [deliveryNoteId]);

    console.log('delivery note ', deliveryNote)

    const handleStatusChange = async () => {
        try {
            setUpdating(true);
            const response = await updateDeliveryNoteStatus(deliveryNoteId, newStatus);
            setDeliveryNote(response.data);
            setError('');
        } catch (err) {
            setError('Erreur lors de la mise à jour du statut');
            console.error(err);
        } finally {
            setUpdating(false);
        }
    };

    const generatePDF = () => {
    if (!deliveryNote) return;
    
    const doc = new jsPDF();
    
    // Configuration de base
    doc.setFont("helvetica", "normal");
    
    // En-tête avec informations de l'entreprise (aligné à droite)
    doc.setFontSize(10);
    doc.text("Zone Industrielle, Diamniadio,", 200, 20, { align: 'right' });
    doc.text("En Face Du Marché D'intérêt National,", 200, 26, { align: 'right' });
    doc.text("Dakar, Sénégal, Tel: +221 78 655 88 88", 200, 32, { align: 'right' });
    
    // Titre du document et date
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(`BON DE LIVRAISON N° ${deliveryNote.referenceNumber}`, 14, 50);
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(formatDate(deliveryNote.deliveryDate), 200, 50, { align: 'right' });
    
    // Référence de commande
    doc.setFontSize(12);
    doc.text(`Référence commande : ${deliveryNote.orderReference}`, 14, 60);
    
    // Section client avec fond grisé (simulé avec un rectangle)
    doc.setFillColor(224, 224, 224); // Couleur grise (#e0e0e0)
    doc.rect(14, 68, 182, 8, 'F'); // Rectangle rempli
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("CLIENT", 16, 74);
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(deliveryNote.clientName, 14, 82);
    doc.text(deliveryNote.clientAddress || "Adresse non spécifiée", 14, 88);
    
    // Calcul de la quantité totale livrée
    const totalOrderedQuantity = deliveryNote.items.reduce((sum, item) => sum + item.deliveredQuantity, 0);
    
    // Tableau des produits avec style adapté
    const items = deliveryNote.items.map((item, index) => [
        index + 1,
        `${item.productCode} / ${item.productName}`,
        item.deliveredQuantity,
        item.notes || ''
    ]);
    
    // Ajouter la ligne total
    // items.push([
    //     'TOTAL',
    //     '',
    //     '',
    //     totalOrderedQuantity,
    //     '',
    //     ''
    // ]);
    
    autoTable(doc, {
        startY: 95,
        head: [['N°', 'PRODUIT', 'QUANTITE LIVRÉE', 'OBSERVATIONS']],
        body: items,
        styles: { 
            fontSize: 10,
            cellPadding: 3
        },
        headStyles: { 
            fillColor: [181, 131, 90], // Couleur bronze (#b5835a)
            textColor: 255,
            fontStyle: 'bold',
            halign: 'left',
        },
        columnStyles: {
            0: { cellWidth: 15, halign: 'left' }, // N°
            1: { cellWidth: 65 }, // PRODUIT
            2: { cellWidth: 25, halign: 'left' }, // QTÉ LIVRÉE
            3: { cellWidth: 35 } // OBSERVATIONS
        },
        didParseCell: function(data) {
            // Style spécial pour la ligne TOTAL
            if (data.row.index === items.length - 1) {
                data.cell.styles.fontStyle = 'bold';
                data.cell.styles.fillColor = [240, 240, 240];
            }
        }
    });
    
    const finalY = doc.lastAutoTable.finalY;
    
    // Section signatures
    doc.setFontSize(10);
    doc.text("Signature du livreur", 200, finalY + 40, { align: 'right' });
    doc.text("Signature du client", 14, finalY + 40);
    
    // Notes (si présentes)
    if (deliveryNote.notes) {
        doc.setFont("helvetica", "bold");
        doc.text("Notes:", 14, finalY + 60);
        doc.setFont("helvetica", "normal");
        doc.text(deliveryNote.notes, 14, finalY + 68);
    }
    
    // Pied de page avec ligne de séparation
    const pageHeight = doc.internal.pageSize.height;
    
    // Ligne de séparation
    doc.setDrawColor(221, 221, 221);
    doc.line(14, pageHeight - 40, 196, pageHeight - 40);
    
    // Texte du pied de page
    doc.setFontSize(9);
    doc.setTextColor(102, 102, 102);
    doc.text("Zone Industrielle, Diamniadio,", 105, pageHeight - 30, { align: 'center' });
    doc.text("En Face Du Marché D'intérêt National,", 105, pageHeight - 25, { align: 'center' });
    doc.text("Dakar, Sénégal", 105, pageHeight - 20, { align: 'center' });
    doc.text("Tel: +221 78 655 88 88", 105, pageHeight - 15, { align: 'center' });
    
    // Sauvegarde
    doc.save(`BonLivraison_${deliveryNote.referenceNumber}.pdf`);
};

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING':
                return <Badge bg="warning">En attente</Badge>;
            case 'IN_TRANSIT':
                return <Badge bg="primary">En transit</Badge>;
            case 'DELIVERED':
                return <Badge bg="success">Livré</Badge>;
            case 'RETURNED':
                return <Badge bg="danger">Retourné</Badge>;
            default:
                return <Badge bg="secondary">{status}</Badge>;
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                <Spinner animation="border" variant="primary" />
                <span className="ms-2">Chargement du bon de livraison...</span>
            </div>
        );
    }

    if (!deliveryNote && !loading) {
        return (
            <Alert variant="danger">
                Ce bon de livraison n'existe pas ou a été supprimé.
            </Alert>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3>Bon de livraison - {deliveryNote?.referenceNumber}</h3>
                <div>
                    <Button variant="outline-secondary" className="me-2" onClick={() => onCancel()}>
                        <ArrowLeft size={16} className="me-1" />
                        Retour
                    </Button>
                    {/* <Button variant="outline-primary" onClick={generatePDF}>
                        <Printer size={16} className="me-1" />
                        Imprimer
                    </Button> */}
                </div>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            <Row>
                <Col md={8}>
                    <Card className="mb-4">
                        <Card.Header>
                            <h5 className="mb-0">Détails de la livraison</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <strong>Référence:</strong> {deliveryNote?.referenceNumber}
                                    </div>
                                    <div className="mb-3">
                                        <strong>Commande:</strong> {deliveryNote?.orderReference}
                                    </div>
                                    <div className="mb-3">
                                        <strong>Client:</strong> {deliveryNote?.clientName}
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <strong>Date de création:</strong> {formatDate(deliveryNote?.createdAt)}
                                    </div>
                                    <div className="mb-3">
                                        <strong>Date de livraison:</strong> {formatDate(deliveryNote?.deliveryDate)}
                                    </div>
                                    {/* <div className="mb-3">
                                        <strong>Statut:</strong> {getStatusBadge(deliveryNote?.status)}
                                    </div> */}
                                </Col>
                            </Row>

                            {deliveryNote?.notes && (
                                <div className="mb-0">
                                    <strong>Notes:</strong>
                                    <p className="mb-0 mt-1">{deliveryNote?.notes}</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">Produits livrés</h5>
                        </Card.Header>
                        <Card.Body>
                            <Table striped bordered>
                                <thead>
                                    <tr>
                                        <th className="text-center">Référence</th>
                                        <th className="text-center">Produit</th>
                                        {/* <th>Qté Commandée</th> */}
                                        <th className="text-center">Quantité Livrée</th>
                                        {/* <th>Unité</th> */}
                                        <th className="text-center">Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {deliveryNote?.items.map((item) => (
                                        <tr key={item.id}>
                                            <td className="text-center">{item.productCode}</td>
                                            <td className="text-center">{item.productName}</td>
                                            {/* <td className="text-center">{item.quantity}</td> */}
                                            <td className="text-center">{item.deliveredQuantity}</td>
                                            {/* <td>{item.productUnit || 'Unité'}</td> */}
                                            <td className="text-center">{item.notes || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={4}>
                    {/* <Card className="mb-4">
                        <Card.Header>
                            <h5 className="mb-0">Gestion du statut</h5>
                        </Card.Header>
                        <Card.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>Statut actuel</Form.Label>
                                <div>{getStatusBadge(deliveryNote?.status)}</div>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Status</Form.Label>
                                <Form.Select 
                                    value={newStatus}
                                    onChange={(e) => setNewStatus(e.target.value)}
                                    disabled={updating}
                                >
                                     <option value="PENDING">En attente</option>
                                    <option value="IN_TRANSIT">En transit</option> 
                                    <option value="DELIVERED">Livré</option>
                                    <option value="RETURNED">Retourné</option> 
                                </Form.Select>
                            </Form.Group>

                            <Button 
                                variant="primary" 
                                className="w-100"
                                onClick={handleStatusChange}
                                disabled={updating || newStatus === deliveryNote?.status}
                            >
                                {updating ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Mise à jour...
                                    </>
                                ) : (
                                    <>
                                        <Save size={16} className="me-1" />
                                        Mettre à jour le statut
                                    </>
                                )}
                            </Button> 
                        </Card.Body>
                    </Card> */}

                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">Informations additionnelles</h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-3">
                                <strong>Créé par:</strong> {deliveryNote?.createdByName}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default DeliveryNoteDetails;
